import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import Header from '../../../components/Header/Header';
import { investorService, InvestorDashboardDto, JourneyListItem, PersonAccountGroup, PendingActionRow } from '../../../services/investor.service';
import { setActiveInvestorJourney } from '../../../services/investorJourneyScope';
import { productLinkService, ProductLinkDto } from '../../../services/productLink.service';
import { profileService, ServiceProviderDto } from '../../../services/profile.service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { useJourneyKycGate } from '../../../hooks/useJourneyKycGate';
import { sowService } from '../../../services/sow.service';
import { SowAgreementModal } from '../MyConsents/SowAgreementModal';
import { useSmartUploadEnabled } from '../../../services/featureFlags.service';
// import './InvestorDashboard.scss';

/**
 * Module-level cache so returning to the dashboard renders instantly from the last
 * load (stale-while-revalidate) instead of showing a spinner and cold-fetching again.
 * Lives for the app session; cleared on a full page reload / logout.
 */
let dashboardCache: {
  dashboardData: InvestorDashboardDto | null;
  journeys: JourneyListItem[];
  personGroups: PersonAccountGroup[];
  serviceProviders: ServiceProviderDto[];
} | null = null;

export const InvestorDashboard: React.FC = () => {
  const navigate = useNavigate();
  // Smart Upload server switch: the "Journey documents" button opens it, so it shows only when on.
  const smartUploadEnabled = useSmartUploadEnabled();
  // Show the spinner only on the very first load; on return, render cached data immediately.
  const [loading, setLoading] = useState(!dashboardCache);
  const [dashboardData, setDashboardData] = useState<InvestorDashboardDto | null>(dashboardCache?.dashboardData ?? null);
  const [journeys, setJourneys] = useState<JourneyListItem[]>(dashboardCache?.journeys ?? []);

  // UC2: pending new-product introductions awaiting this investor's approval (confirm-on-login).
  const [pendingProductLinks, setPendingProductLinks] = useState<ProductLinkDto[]>([]);
  const [processingProductLink, setProcessingProductLink] = useState<number | null>(null);
  // The introductions surface as a modal popup, not a banner. Closing it without acting
  // dismisses those specific link ids for this browser session only (sessionStorage) —
  // the row stays in My Pending Actions (server-driven), and a NEW introduction has a
  // fresh id so it pops up even after earlier dismissals. Next login: undismissed
  // pending links pop up again.
  const [showProductIntroModal, setShowProductIntroModal] = useState(false);
  // Blocking SOW popup — see fetchDashboardData.
  const [sowGateOpen, setSowGateOpen] = useState(false);
  const [sowGateBusy, setSowGateBusy] = useState(false);
  const handleSowGateAgree = async () => {
    setSowGateBusy(true);
    try {
      await sowService.agreeMe('DASHBOARD');
      setSowGateOpen(false);
      setDashboardData(prev => (prev ? { ...prev, sowAgreed: true } : prev));
      toast.success('Statement of Work agreed');
      // Pending actions are a separate call: refresh them too, or the "Statement of Work Consent"
      // row stays on the dashboard until the page is reloaded (client 2026-10-25, item 3).
      await Promise.all([fetchDashboardData(scopedJourneyId), fetchPendingActions()]);
    } catch (error) {
      console.error('[InvestorDashboard] SOW agree failed:', error);
      toast.error('Could not record your agreement. Please try again.');
    } finally {
      setSowGateBusy(false);
    }
  };
  const PRODUCT_INTRO_DISMISS_KEY = 'fs.productIntroDismissedIds';
  const readDismissedIntroIds = (): number[] => {
    try { return JSON.parse(sessionStorage.getItem(PRODUCT_INTRO_DISMISS_KEY) || '[]'); }
    catch { return []; }
  };
  const dismissProductIntroModal = () => {
    try {
      const ids = new Set([...readDismissedIntroIds(), ...pendingProductLinks.map((l) => l.id)]);
      sessionStorage.setItem(PRODUCT_INTRO_DISMISS_KEY, JSON.stringify(Array.from(ids)));
    } catch { /* storage unavailable — modal simply reopens next visit */ }
    setShowProductIntroModal(false);
  };
  // Open when an undismissed pending link exists; close automatically once the last
  // link in view is approved/declined.
  useEffect(() => {
    if (pendingProductLinks.length === 0) {
      setShowProductIntroModal(false);
      return;
    }
    const dismissed = new Set(readDismissedIntroIds());
    if (pendingProductLinks.some((l) => !dismissed.has(l.id))) {
      setShowProductIntroModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProductLinks]);

  // B2 (UC1): the Service Providers this investor works with, rolled up across journeys.
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderDto[]>(dashboardCache?.serviceProviders ?? []);

  // A.4 dashboard filters — two dropdowns, both default All. SP = Dataverse spRef;
  // Accounts = person-based (person layer). A journey is EXCLUDED only when positively
  // attributed elsewhere (fail-open): null spRef / unknown person membership never hides it.
  const [selectedSpRef, setSelectedSpRef] = useState<string>('ALL');
  const [selectedPersonId, setSelectedPersonId] = useState<number | 'ALL'>('ALL');
  const [spMenuOpen, setSpMenuOpen] = useState(false);
  // A.5: status filter on the journey table — Completed | Pending | All, default Pending.
  // Pending = every non-completed status (in progress / in review / abandoned / unset).
  const [journeyStatusFilter, setJourneyStatusFilter] = useState<'PENDING' | 'COMPLETED' | 'ALL'>('PENDING');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  // Person layer (Phase 3): real person groups from /me/persons. Empty = endpoint
  // unavailable → the Accounts dropdown falls back to a single self group (fail-safe).
  const [personGroups, setPersonGroups] = useState<PersonAccountGroup[]>(dashboardCache?.personGroups ?? []);
  // UX task B: pending consents/permissions rows for the My Pending Actions card.
  const [pendingActionRows, setPendingActionRows] = useState<PendingActionRow[]>([]);

  const navigateToJourney = (item: JourneyListItem) => {
    // Remember the opened journey so the destination (and any per-journey page reached next) scopes to
    // it — actionRoute may be a journey-less step route (e.g. /investor/onboarding), so the store, not
    // the URL, is what carries the journey.
    // C8 (audit 2026-08-25): only SET, never clear — a list row without a journeyId used to
    // wipe the scope, silently dropping every later step page back to the PRIMARY journey.
    if (item.journeyId) {
      setActiveInvestorJourney(item.journeyId);
    }
    navigate(item.actionRoute || (item.journeyId ? `/investor/journey/${item.journeyId}` : '/investor/journeys'));
  };

  // Benefit nudge + per-journey consent + address reconcile (shared with the journey list).
  const { openJourney, modals: journeyGateModals } = useJourneyKycGate(navigateToJourney, 'Dashboard');

  // ── A.4 filter derivation (SP × Accounts, AND-composed, fail-open) ─────────
  // A journey is excluded only when POSITIVELY attributed to a different SP/person:
  // null spRef (PMS/legacy) or unknown person membership never hides it.
  const guidEq = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.toLowerCase() === b.toLowerCase();
  const selectedGroup = selectedPersonId === 'ALL'
    ? null
    : personGroups.find((g) => g.personId === selectedPersonId) || null;
  const visibleJourneys = journeys.filter((j) => {
    if (selectedSpRef !== 'ALL' && j.spRef && !guidEq(j.spRef, selectedSpRef)) return false;
    if (selectedGroup && j.journeyId
      && personGroups.some((g) => (g.journeys || []).some((gj) => gj.journeyId === j.journeyId))
      && !(selectedGroup.journeys || []).some((gj) => gj.journeyId === j.journeyId)) return false;
    return true;
  });
  // Journey-scope the product cards ONLY while a filter is actively applied AND it narrows
  // to exactly one journey. All/All always fetches unscoped — byte-identical to the old
  // default view even for single-journey investors (audit F3).
  const filtersActive = selectedSpRef !== 'ALL' || selectedPersonId !== 'ALL';
  // A.5: the journey TABLE additionally filters by status. Display-only — it never feeds
  // scopedJourneyId, so the product cards keep scoping off the account/SP filters alone.
  const statusFilteredJourneys = visibleJourneys.filter((j) => {
    if (journeyStatusFilter === 'ALL') return true;
    const done = (j.status || '').toUpperCase() === 'COMPLETED';
    return journeyStatusFilter === 'COMPLETED' ? done : !done;
  });
  const scopedJourneyId = filtersActive && visibleJourneys.length === 1
    ? visibleJourneys[0].journeyId
    : undefined;

  useEffect(() => {
    // Dashboard fetch is driven by the account-selector effect below (fires on mount as 'ALL').
    fetchJourneys();
    fetchPendingProductLinks();
  }, []);

  // A1: (re)fetch the dashboard scoped to the selected account. 'ALL' → identity-level default view;
  // a specific journey → Account Summary + Pending Actions computed for that journey.
  // P3.3: mirror the selection into the active-journey scope so the onboarding-document and
  // physical-submission pages fetch/store against the same journey the investor picked here.
  useEffect(() => {
    // C8 (audit 2026-08-25): only SET when the filters narrow to exactly one journey.
    // This effect used to CLEAR the scope on every wide-filter render (All/All fires on
    // mount), wiping the journey the investor had selected earlier — every step page
    // opened afterwards silently fell back to the PRIMARY journey. The dashboard's own
    // fetch still passes scopedJourneyId (undefined = identity view) explicitly.
    if (scopedJourneyId) {
      setActiveInvestorJourney(scopedJourneyId);
    }
    fetchDashboardData(scopedJourneyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedJourneyId]);

  // Close both filter menus on outside click.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setSpMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // Keep the session cache in sync so the next return paints instantly.
  useEffect(() => {
    dashboardCache = { dashboardData, journeys, personGroups, serviceProviders };
  }, [dashboardData, journeys, personGroups, serviceProviders]);

  const fetchJourneys = async () => {
    try {
      const data = await investorService.getJourneys();
      setJourneys(data || []);
      // B2: getJourneys() write-throughs the investor_service_provider rollup on the backend,
      // so read the SP mapping only after it resolves (avoids an empty first read).
      fetchServiceProviders();
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching journeys:', error);
    }
    // Person layer: group headers for the account selector. Best-effort — a failure
    // just keeps the flat fallback grouping; it must never affect the dashboard data.
    try {
      setPersonGroups(await investorService.getPersonAccounts() || []);
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching person groups:', error);
    }
    fetchPendingActions();
  };

  // UX task B: the pending consents/permissions rows (fail-open server-side). Re-fetched
  // after any resolving action so the row disappears without a page reload.
  const fetchPendingActions = async () => {
    try {
      setPendingActionRows(await investorService.getPendingActions() || []);
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching pending actions:', error);
    }
  };

  // B2 (UC1): the investor's Service Providers, rolled up across journeys.
  const fetchServiceProviders = async () => {
    try {
      setServiceProviders(await profileService.getServiceProviders());
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching service providers:', error);
    }
  };

  const fetchDashboardData = async (selectedJourneyId?: string) => {
    try {
      const data = await investorService.getDashboard(selectedJourneyId);
      setDashboardData(data);
      // SOW gate (client 2026-10-12): while the SOW is pending (never agreed, or revoked),
      // the dashboard opens the agreement as a blocking popup with the tick pre-set. Decided
      // on FRESH data only — the session cache can still say "not agreed" right after the
      // Consent Centre redirects here, which would reopen it after agreeing.
      setSowGateOpen(data.sowAgreed === false);
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── UC2: pending new-product introductions (confirm-on-login, journeys BRD §4) ──
  const fetchPendingProductLinks = async () => {
    try {
      setPendingProductLinks(await productLinkService.getPending());
    } catch (error) {
      console.error('[InvestorDashboard] Error fetching pending product links:', error);
    }
  };

  const handleApproveProductLink = async (link: ProductLinkDto) => {
    setProcessingProductLink(link.id);
    try {
      await productLinkService.approve(link.id);
      toast.success(`${link.productName || 'New product'} added — its journey is now available.`);
      await Promise.all([fetchPendingProductLinks(), fetchJourneys(), fetchPendingActions()]);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to approve the new product');
    } finally {
      setProcessingProductLink(null);
    }
  };

  const handleDeclineProductLink = async (link: ProductLinkDto) => {
    if (!globalThis.confirm(`Decline the introduction for ${link.productName || 'this product'}?`)) return;
    setProcessingProductLink(link.id);
    try {
      await productLinkService.decline(link.id);
      toast.info('Introduction declined.');
      await fetchPendingProductLinks();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to decline');
    } finally {
      setProcessingProductLink(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!dashboardData) {
    return (
      <div className="facilon-dashboard-wrapper">
        <Header />
        <div className="container-fluid dashboard-container-main">
          <div className="alert alert-warning mt-4">
            Unable to load dashboard data. Please refresh.
          </div>
        </div>
      </div>
    );
  }

  // UX task B (2026-08-22): My Pending Actions = pending CONSENTS + PERMISSIONS, person-
  // labelled — journey step nudges retired (My Onboarding Journey covers progress). The
  // Action column routes each row into its EXISTING flow.
  const handlePendingAction = (row: PendingActionRow) => {
    switch (row.type) {
      case 'SERVICE_AGENT_CONSENT':
        // Live-test feedback 2026-08-22: redirect to the Permissions Center (the respective
        // screen) rather than opening the accept popup here.
        navigate('/investor/delegations');
        return;
      case 'PRODUCT_APPROVAL': {
        const link = pendingProductLinks.find((l) => String(l.id) === row.refId);
        if (link) handleApproveProductLink(link);
        return;
      }
      case 'SOW_CONSENT': {
        // Family person: their SOW is asked inside their journey's gate; login person: the
        // Consent Centre deep link opens the SOW modal directly.
        if (row.journeyId && row.personId != null && row.personId !== (personGroups[0]?.personId ?? null)) {
          openJourney({ journeyId: row.journeyId, product: row.productName });
        } else {
          navigate('/investor/consents?open=sow');
        }
        return;
      }
      case 'KYC_CONSENT': {
        // openJourney runs the gate → the reuse popup (R1) handles consent.
        if (row.journeyId) openJourney({ journeyId: row.journeyId, product: row.productName });
        return;
      }
      case 'JOURNEY_SUBMIT_REQUIRED': {
        // Journey is ready but only the investor can final-submit (no submit-capable
        // agent) — into the journey, where the Final Submit tab awaits (2026-09-05).
        if (row.journeyId) openJourney({ journeyId: row.journeyId, product: row.productName });
        return;
      }
      case 'PLATFORM_CONSENT':
      default:
        navigate('/investor/consents');
    }
  };

  // My Appointment card hidden for now — mock data kept for when it's re-enabled
  // const appointments = [
  //   { product: 'HYSA Consultation', provider: 'Global Wealth', date: 'May 15, 2026', status: 'CONFIRMED', statusColor: 'bg-[#ecfdf5] text-[#10b981] border-none' },
  //   { product: 'Equity Review', provider: 'Northern Trust', date: 'May 22, 2026', status: 'PENDING', statusColor: 'bg-[#fff8f0] text-[#f59e0b] border-none' },
  //   { product: 'Tax Strategy', provider: 'Facilon Prime', date: 'June 02, 2026', status: 'CONFIRMED', statusColor: 'bg-[#ecfdf5] text-[#10b981] border-none' },
  // ];

  // Identity cards (profile, documents, permissions, DSR, consents) are NEVER scoped — one person.
  const investorFirstName = dashboardData?.investor?.firstName || 'Investor';
  const selectedSp = selectedSpRef === 'ALL'
    ? null
    : serviceProviders.find((sp) => guidEq(sp.spRef, selectedSpRef)) || null;

  return (
    <div className="facilon-dashboard-wrapper font-sans text-gray-800 min-h-[83vh] pb-0">
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">
        <>
          {/* Welcome Banner */}
          <div
            className="text-white rounded shadow-sm mb-3 px-4 py-3 flex flex-col justify-between"
            style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
          >
            <div className="flex justify-between items-start">
              <div className="flex flex-col justify-between items-start mb-0">
                {/* A.2 + person selection: at Accounts=All the banner is person-neutral — just
                    "Welcome", no name, no identity strip. Selecting a person (family-ready:
                    spouse etc. later join the same dropdown) shows THAT person's name+details. */}
                <h1 className="text-[16px] font-bold text-white flex items-center tracking-tight mb-0">
                  Welcome{selectedGroup ? ` ${selectedGroup.displayName}` : ''}
                  {selectedGroup && (
                    <i className="bi bi-pencil-square ml-2 text-[12px] opacity-70 cursor-pointer hover:opacity-100"></i>
                  )}
                </h1>

                {selectedGroup && (
                  <div className="flex flex-wrap items-center gap-2 text-[12px] opacity-90 mt-1">
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-70">Email:</span>
                      <span className="font-semibold">{dashboardData?.investor?.email || 'investor@example.com'}</span>
                    </div>
                    |
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-70">Investor ID:</span>
                      <span className="font-semibold">{dashboardData?.investor?.uniqueCode || '202604096545'}</span>
                    </div>
                    |
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-70">Country of Residence:</span>
                      <span className="font-semibold">{dashboardData?.investor?.countryOfResidence || 'United States'}</span>
                    </div>
                    |
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-70">Nationality:</span>
                      <span className="font-semibold">{dashboardData?.investor?.nationality || 'American'}</span>
                    </div>
                    |
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-70">Mobile:</span>
                      <span className="font-semibold">{dashboardData?.investor?.mobileNumber || '+1 (555) 123-4567'}</span>
                    </div>
                  </div>
                )}
              </div>


              {/* A.4: two dashboard filters, both default All.
                    Service Provider — dynamic from the investor's actual SPs (B2 rollup).
                    Accounts — person-based (person layer /me/persons; family-ready). */}
              <div className="flex items-center gap-2" ref={filtersRef}>
                {/* Service Provider filter */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setSpMenuOpen((o) => !o); setAccountMenuOpen(false); }}
                    className="bg-white/10 border border-white/25 rounded px-3 py-1.5 flex items-center text-[11px] font-semibold cursor-pointer hover:bg-white/20 transition-colors text-white shadow-sm"
                  >
                    <span className="opacity-70 mr-1.5">Service Provider:</span>
                    <span>{selectedSp?.spName || 'All'}</span>
                    <i className="bi bi-chevron-down ml-2 text-[9px]"></i>
                  </button>
                  {spMenuOpen && (
                    <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => { setSelectedSpRef('ALL'); setSpMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 transition-colors border-0 bg-transparent cursor-pointer ${selectedSpRef === 'ALL' ? 'font-bold text-[#b0842b]' : 'text-slate-700'}`}
                      >
                        All service providers
                      </button>
                      {serviceProviders.map((sp) => (
                        <button
                          key={sp.spRef || sp.spName}
                          type="button"
                          onClick={() => { setSelectedSpRef(sp.spRef || 'ALL'); setSpMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 transition-colors border-0 bg-transparent cursor-pointer ${guidEq(sp.spRef, selectedSpRef) ? 'font-bold text-[#b0842b]' : 'text-slate-700'}`}
                        >
                          <span className="block">{sp.spName || 'Service Provider'}</span>
                          <span className="block text-[9px] text-slate-400">
                            {sp.spType || ''}{sp.journeyCount ? ` · ${sp.journeyCount} ${sp.journeyCount === 1 ? 'journey' : 'journeys'}` : ''}
                          </span>
                        </button>
                      ))}
                      {serviceProviders.length === 0 && (
                        <div className="px-3 py-2 text-[11px] text-slate-400">No service providers yet</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Accounts filter — person-based */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setAccountMenuOpen((o) => !o); setSpMenuOpen(false); }}
                    className="bg-[#B0842B] border-[#B0842B] rounded px-3 py-1.5 flex items-center text-[11px] font-semibold cursor-pointer hover:bg-[#9A7325] transition-colors text-white shadow-sm"
                  >
                    <span className="opacity-70 mr-1.5">Accounts:</span>
                    <span>{selectedGroup?.displayName || 'All'}</span>
                    <i className="bi bi-chevron-down ml-2 text-[9px]"></i>
                  </button>
                  {accountMenuOpen && (
                    <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => { setSelectedPersonId('ALL'); setAccountMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 transition-colors border-0 bg-transparent cursor-pointer ${selectedPersonId === 'ALL' ? 'font-bold text-[#b0842b]' : 'text-slate-700'}`}
                      >
                        All accounts
                      </button>
                      {(personGroups.length > 0
                        ? personGroups
                        : [{ personId: null, displayName: investorFirstName, journeys } as PersonAccountGroup]
                      ).map((g) => (
                        <button
                          key={`p-${g.personId ?? 'self'}`}
                          type="button"
                          disabled={g.personId == null}
                          onClick={() => { if (g.personId != null) { setSelectedPersonId(g.personId); setAccountMenuOpen(false); } }}
                          className={`w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 transition-colors border-0 bg-transparent cursor-pointer disabled:cursor-default ${selectedPersonId === g.personId ? 'font-bold text-[#b0842b]' : 'text-slate-700'}`}
                        >
                          <span className="block">{g.displayName}</span>
                          <span className="block text-[9px] text-slate-400">
                            {(g.journeys || []).length} {(g.journeys || []).length === 1 ? 'account' : 'accounts'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* TOP ROW: 2 Column Layout (50% / 50% on Desktop & Tablet Landscape, 100% on Small Devices & Tablet Portrait) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3 items-start">

            {/* Column 1: My Pending Action */}
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] flex flex-col h-full">
              <div className="p-3 flex justify-between items-center border-b border-[#e2e8f0]">
                <h2 className="text-[14px] font-bold text-slate-800 flex items-center tracking-tight mb-0">
                  <span className="w-7 h-7 rounded-full bg-[#e8edf2] text-[#334155] flex items-center justify-center mr-2"><i className="bi bi-clock-history text-[13px]"></i></span> My Pending Action
                </h2>
                <span className="bg-[#fdf6e2] text-[#b0842b] text-[10px] font-semibold px-2.5 py-0.5 rounded uppercase tracking-wider">
                  {pendingActionRows.length} PENDING
                </span>
              </div>

              <div className="px-0 py-0 flex flex-col justify-start">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#f1f5f9]">
                      <tr className="text-[10px] font-bold text-[#5a6e85] tracking-wider">
                        <th className="px-3 py-2 w-auto">Product</th>
                        <th className="px-3 py-2 w-auto">Pending Item</th>
                        <th className="px-3 py-2 w-auto">Service Provider</th>
                        <th className="px-3 py-2 w-auto">Account</th>
                        <th className="px-3 py-2 w-[10%] text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingActionRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-[11px] text-slate-400">
                            You're all caught up — no pending actions.
                          </td>
                        </tr>
                      ) : (
                        pendingActionRows.map((row, idx) => (
                          <tr key={`${row.type}-${row.refId ?? idx}`} className="border-b border-[#e2e8f0] last:border-0 hover:bg-slate-50/50 transition-colors">
                            <td className="px-3 py-3 text-[12px] font-bold text-slate-800 align-middle">{row.productName || 'Onboard'}</td>
                            <td className="px-3 py-3 text-[12px] text-[#64748b] align-middle">{row.pendingItem || '-'}</td>
                            <td className="px-3 py-3 text-[12px] text-[#64748b] align-middle">{row.serviceProviderName || '—'}</td>
                            <td className="px-3 py-3 text-[12px] text-[#64748b] align-middle">{row.personName || investorFirstName}</td>
                            <td className="px-3 py-3 text-right align-middle">
                              <div className="flex justify-end">
                                <button
                                  onClick={() => handlePendingAction(row)}
                                  className="px-2.5 py-1 text-[10px] font-semibold text-white bg-[#052f55] hover:bg-[#042442] rounded transition-colors cursor-pointer"
                                  title={row.pendingItem || 'Open'}
                                >
                                  {/* Button copy states the action the row leads to, per type —
                                      "Resolve" told the investor nothing (user 2026-09-04). */}
                                  {{
                                    PRODUCT_APPROVAL: 'Approve',
                                    SOW_CONSENT: 'Agree',
                                    SERVICE_AGENT_CONSENT: 'Accept',
                                    KYC_CONSENT: 'Review',
                                    PLATFORM_CONSENT: 'Review',
                                    JOURNEY_SUBMIT_REQUIRED: 'Submit',
                                  }[row.type] ?? 'Open'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>

            {/* Column 2: My Onboarding Status */}
            <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] flex flex-col h-full">
              <div className="p-2 flex justify-between items-center border-b border-[#e2e8f0]">
                <h2 className="text-[14px] font-bold text-slate-800 flex items-center tracking-tight mb-0">
                  <span className="w-6 h-6 rounded-full bg-[#052f55]/10 text-[#052f55] flex items-center justify-center mr-2"><i className="bi bi-rocket text-[12px]"></i></span> My Onboarding Journey
                </h2>
                <div className="flex items-center gap-2">
                  {/* A.5 status filter — default Pending */}
                  <div className="flex items-center rounded overflow-hidden border border-[#e2e8f0]">
                    {(['PENDING', 'COMPLETED', 'ALL'] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setJourneyStatusFilter(opt)}
                        className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer border-0 ${journeyStatusFilter === opt
                          ? 'bg-[#052f55] text-white'
                          : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                      >
                        {opt === 'ALL' ? 'All' : opt === 'PENDING' ? 'Pending' : 'Completed'}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate('/investor/journeys')}
                    className="px-2 py-1 text-[10px] font-medium text-[#b0842b] bg-[#b0842b]/10 hover:bg-[#b0842b]/20 rounded transition-colors cursor-pointer"
                  >
                    Facilon Status
                  </button>
                </div>
              </div>

              <div className="px-0 py-0 flex flex-col justify-start">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-[#f1f5f9]">
                      <tr className="text-[10px] font-bold text-[#5a6e85] tracking-wider">
                        {/* UAT item 6 (2026-10-14): whose account each journey is — two clients can share one login. */}
                        <th className="px-3 py-2 w-auto">Account</th>
                        <th className="px-3 py-2 w-auto">Service Provider</th>
                        <th className="px-3 py-2 w-auto">Product</th>
                        <th className="px-3 py-2 w-auto">Status</th>
                        <th className="px-3 py-2 w-[10%] text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusFilteredJourneys.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-2 py-4 text-center text-[11px] text-slate-400">
                            {visibleJourneys.length === 0
                              ? 'No onboarding journeys assigned yet.'
                              : journeyStatusFilter === 'COMPLETED'
                                ? 'No completed journeys yet.'
                                : 'No pending journeys — everything is completed.'}
                          </td>
                        </tr>
                      ) : (
                        statusFilteredJourneys.map((item, idx) => {
                          const status = (item.status || 'IN PROGRESS').toUpperCase();
                          const completed = status === 'COMPLETED';
                          const statusColor = completed
                            ? 'text-[#3e6f7c] bg-[#3e6f7c]/10'
                            : status === 'ABANDONED'
                              ? 'text-[#ef4444] bg-[#fef2f2]'
                              : status === 'IN REVIEW'
                                ? 'text-[#f59e0b] bg-[#fff8f0]'
                                : 'text-[#052f55] bg-[#052f55]/10';
                          const barColor = completed ? 'bg-[#3e6f7c]' : status === 'IN PROGRESS' ? 'bg-[#052f55]' : status === 'ABANDONED' ? 'bg-[#ef4444]' : 'bg-[#f59e0b]';
                          const progress = typeof item.progress === 'number' ? item.progress : (completed ? 100 : status === 'ABANDONED' ? 100 : 0);
                          const goToJourney = () => openJourney(item);
                          return (
                            <tr key={item.journeyId || idx} className="border-b border-[#e2e8f0] last:border-0 hover:bg-slate-50/50 transition-colors">
                              <td className="px-2 py-2 text-[11px] font-semibold text-slate-700 leading-tight align-middle">
                                {item.personName || personGroups[0]?.displayName || investorFirstName}
                              </td>
                              <td className="px-2 py-2 align-middle">
                                <div className="text-[11px] font-bold text-slate-800 leading-tight">{item.serviceProviderName || '-'}</div>
                                <div className="text-[9px] text-slate-400 mt-0.5">{item.productCode ? `Code: ${item.productCode}` : '-'}</div>
                              </td>
                              <td className="px-2 py-2 text-[11px] text-slate-600 leading-tight align-middle">{item.product || '-'}</td>
                              <td className="px-2 py-2 align-middle">
                                <span className={`inline-flex items-center text-[7.5px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${statusColor}`}>
                                  {status === 'ABANDONED' ? 'DISCONTINUED' : status}
                                </span>
                                <div className="w-12 h-[3px] bg-[#e2e8f0] rounded-full mt-1.5 overflow-hidden">
                                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${progress}%` }}></div>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right align-middle">
                                <div className="flex justify-end gap-1.5">
                                  {/* D3: this journey's Smart Upload / documents view.
                                      Hidden once COMPLETED — a submitted journey takes no
                                      more uploads; the eye (view) is the remaining action. */}
                                  {item.journeyId && !completed && smartUploadEnabled === true && (
                                    <button
                                      onClick={() => navigate(`/investor/documents-center?journeyId=${encodeURIComponent(item.journeyId!)}`)}
                                      title="Journey documents"
                                      className="w-7 h-7 rounded-full bg-[#b0842b]/10 hover:bg-[#b0842b]/20 text-[#b0842b] flex items-center justify-center transition-colors cursor-pointer"
                                    >
                                      <i className="bi bi-file-earmark-arrow-up text-[12px]"></i>
                                    </button>
                                  )}
                                  <button
                                    onClick={goToJourney}
                                    className="w-7 h-7 rounded-full bg-[#052f55]/10 hover:bg-[#052f55]/20 text-[#052f55] hover:text-[#042442] flex items-center justify-center transition-colors cursor-pointer"
                                  >
                                    {completed ? (
                                      <i className="bi bi-eye text-[12px]"></i>
                                    ) : (
                                      <i className="bi bi-play-fill text-[13px] ml-0.5"></i>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 px-2 pb-2 flex justify-center">
                  <button
                    onClick={() => navigate('/investor/journeys')}
                    className="w-auto px-2 py-1.5 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors flex justify-center items-center cursor-pointer"
                  >
                    View Detailed Journey
                  </button>
                </div>
              </div>
            </div>


            {/* Column 4: My Appointment — hidden for now (Coming Soon)
              <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] flex flex-col">
                <div className="p-2 flex justify-between items-center border-b border-[#e2e8f0]">
                  <h2 className="text-[14px] font-bold text-slate-800 flex items-center tracking-tight mb-0">
                    <i className="bi bi-calendar3 mr-2 text-slate-500"></i> My Appointment
                  </h2>
                  <span className="text-[9px] font-extrabold px-2 py-0.5 rounded uppercase tracking-wider bg-[#fff8f0] text-[#f59e0b] border border-[#f59e0b]/30">
                    Coming Soon
                  </span>
                </div>

                <div className="px-4 pt-2 pb-2 flex flex-col justify-start">
                  <div>
                    <div className="grid grid-cols-12 text-[8px] font-bold text-slate-400 uppercase tracking-wider pb-2 border-b border-[#e2e8f0] mb-1">
                      <div className="col-span-5">PRODUCT</div>
                      <div className="col-span-3">SERVICE PROVIDER</div>
                      <div className="col-span-2">DATE</div>
                      <div className="col-span-2 text-right">STATUS</div>
                    </div>

                    <div className="flex flex-col">
                      {appointments.map((item, idx) => (
                        <div key={idx} className="grid grid-cols-12 items-center p-1 border-b border-[#e2e8f0] last:border-0 hover:bg-slate-50/50 transition-colors">
                          <div className="col-span-5 text-[11px] font-bold text-slate-800 pr-1 leading-tight">
                            {item.product}
                          </div>
                          <div className="col-span-3 text-[11px] text-slate-500 pr-1 leading-tight">{item.provider}</div>
                          <div className="col-span-2 text-[11px] pr-1 leading-tight">
                            <div className="font-semibold text-slate-700">{item.date.split(',')[0]},</div>
                            <div className="text-[9px] text-slate-400 mt-0.5">{item.date.split(',')[1]?.trim()}</div>
                          </div>
                          <div className="col-span-2 text-right flex justify-end">
                            <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${item.statusColor}`}>
                              {item.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3">
                    <button className="w-full py-1.5 text-[10px] font-semibold text-slate-400 border border-slate-200 rounded bg-slate-50 cursor-not-allowed flex justify-center items-center" disabled>
                      <i className="bi bi-calendar3 mr-1.5"></i> View All Appointments
                    </button>
                  </div>
                </div>
              </div>
              */}

          </div>

          {/* Bottom-row cards (My Documents / My Permissions / DSR Center / My Consents)
              removed 2026-08-23 — all four live in the sidebar as full pages; My Pending
              Actions covers the actionable rows. */}
        </>
      </main>

      {/* UC2: pending new-product introductions — modal popup (was a banner). Approve
          activates the journey; Close dismisses for this session only — the row stays
          in My Pending Actions until acted on. */}
      <Modal
        show={showProductIntroModal}
        onHide={dismissProductIntroModal}
        size="lg"
        centered
        backdrop="static"
        container={typeof document === 'undefined' ? undefined : document.body}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-[15px] font-bold text-slate-800">
            <i className="bi bi-box-seam mr-2 text-[#b45309]"></i>
            New product introduction{pendingProductLinks.length > 1 ? 's' : ''} awaiting your approval
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingProductLinks.map((link) => (
            <div key={link.id} className="flex flex-col md:flex-row md:items-center justify-between gap-2 py-2 border-b border-[#e2e8f0] last:border-0">
              <div className="text-[12px] text-slate-700">
                <span className="font-bold">{link.serviceProviderName || 'Your Service Provider'}</span>
                {/* I-16: name instead of "you"; family intros show the introduced person's name. */}
                {' '}has introduced{' '}
                <span className="font-bold">
                  {link.personName
                    || `${dashboardData?.investor?.firstName || ''} ${dashboardData?.investor?.lastName || ''}`.trim()
                    || 'you'}
                </span>
                {' '}for{' '}
                <span className="font-bold">{link.productName || 'a new product'}</span>
                {link.introCode ? <span className="text-slate-400"> ({link.introCode})</span> : null}.
                <span className="block text-[10px] text-slate-500 mt-0.5">
                  Approving adds this product journey to your account. Your verified details and
                  documents are reused only with your consent.
                </span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => handleApproveProductLink(link)}
                  disabled={processingProductLink === link.id}
                  className="px-3 py-1.5 text-[11px] font-semibold text-white bg-[#b0842b] border border-[#b0842b] rounded hover:bg-[#8f6b23] transition-colors cursor-pointer"
                >
                  {processingProductLink === link.id ? 'Saving…' : 'Approve & Continue'}
                </button>
                <button
                  onClick={() => handleDeclineProductLink(link)}
                  disabled={processingProductLink === link.id}
                  className="px-3 py-1.5 text-[11px] font-semibold text-[#b45309] bg-transparent border border-[#b45309]/40 rounded hover:bg-[#b45309]/10 transition-colors cursor-pointer"
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {journeyGateModals}

      {/* SOW gate (client 2026-10-12): pending SOW → blocking agreement, tick pre-set.
          Rendered last so it sits above the other popups. */}
      <SowAgreementModal
        show={sowGateOpen}
        busy={sowGateBusy}
        blocking
        defaultChecked
        onClose={() => undefined}
        onAgree={handleSowGateAgree}
      />





    </div>
  );
};
